/**
 * Never lose what was typed. Drafts live in IndexedDB, keyed by user AND
 * patient so one can never surface under another, are deleted only after the
 * server confirms the write, and are cleared wholesale on sign-out.
 */
import { platform } from '@/platform'
import type { VisitWriteRequest } from '@/api/types'

const PREFIX = 'draft:visit:'

export interface VisitDraft {
  userId: string
  patientId: string
  /** 'new', or the id of the visit being edited. */
  visitId: string
  content: VisitWriteRequest
  savedAt: string
}

export function draftKey(userId: string, patientId: string, visitId: string): string {
  return `${PREFIX}${userId}:${patientId}:${visitId}`
}

export async function loadDraft(
  userId: string,
  patientId: string,
  visitId: string,
): Promise<VisitDraft | null> {
  return platform.storage.get<VisitDraft>(draftKey(userId, patientId, visitId))
}

export async function saveDraft(draft: Omit<VisitDraft, 'savedAt'>): Promise<VisitDraft> {
  const stored: VisitDraft = { ...draft, savedAt: new Date().toISOString() }
  await platform.storage.set(draftKey(draft.userId, draft.patientId, draft.visitId), stored)
  return stored
}

/** Called only after the server has confirmed the write. */
export async function discardDraft(
  userId: string,
  patientId: string,
  visitId: string,
): Promise<void> {
  await platform.storage.remove(draftKey(userId, patientId, visitId))
}

export async function listDraftKeys(): Promise<string[]> {
  return platform.storage.keys(PREFIX)
}

/** The clinic desktop is a shared machine. Sign-out takes everything. */
export async function clearAllLocalData(): Promise<void> {
  await platform.storage.clear()
}

/** True when the draft holds something a doctor would be upset to lose. */
export function draftHasContent(content: VisitWriteRequest): boolean {
  if (content.chief_complaints?.trim()) return true
  if (content.notes?.trim()) return true
  if ((content.test_results ?? []).some((t) => t.test_name?.trim())) return true
  return (content.prescription ?? []).some((item) => item.drug_name?.trim())
}
